import React from 'react';
import { useTranslation } from 'react-i18next';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useNavigate } from 'react-router-dom';
import { toast } from 'react-hot-toast';
import { adminService, IAdminUser, ILoginEvent } from './service/admin.service';
import useAuth from '../auth/feature/auth';
import { ROUTER_LINKS } from '../common/consts/app-keys.const';

const formatDate = (iso: string) =>
  new Date(iso).toLocaleDateString('en-US', { year: 'numeric', month: 'short', day: 'numeric' });

const formatDateTime = (iso: string) =>
  new Date(iso).toLocaleString('en-US', { year: 'numeric', month: 'short', day: 'numeric', hour: 'numeric', minute: '2-digit' });

const parseUserAgent = (ua: string | null): string => {
  if (!ua) return 'Unknown';
  const browsers: [RegExp, string][] = [
    [/Edg\//, 'Edge'], [/OPR\//, 'Opera'], [/Chrome\//, 'Chrome'],
    [/Firefox\//, 'Firefox'], [/Safari\//, 'Safari'],
  ];
  const oses: [RegExp, string][] = [
    [/Windows NT/, 'Windows'], [/Mac OS X/, 'macOS'], [/Linux/, 'Linux'],
    [/Android/, 'Android'], [/iPhone|iPad/, 'iOS'],
  ];
  const browser = browsers.find(([re]) => re.test(ua))?.[1] ?? 'Unknown';
  const os = oses.find(([re]) => re.test(ua))?.[1] ?? 'Unknown';
  return `${browser} · ${os}`;
};

const SubscriptionBadge = ({ user }: { user: IAdminUser }) => {
  if (user.subscriptionTier === 'enterprise') return (
    <span className="text-[11px] font-semibold px-[6px] py-[1px] rounded-[4px] bg-[#F0F9FF] text-[#7C3AED] border border-[#C4B5FD]">Enterprise</span>
  );
  if (user.isProGifted) return (
    <span className="text-[11px] font-semibold px-[6px] py-[1px] rounded-[4px] bg-[#FFFBEB] text-[#92400E]">⭐ Pro</span>
  );
  if (user.subscriptionTier === 'trial') return (
    <span className="text-[11px] font-semibold px-[6px] py-[1px] rounded-[4px] bg-[#F8FCFF] text-[#0ea5c9] border border-[#BCEBFB]">Trial</span>
  );
  if (user.hasActiveSubscription && user.subscriptionCancelledAt) return (
    <span className="text-[11px] font-semibold px-[6px] py-[1px] rounded-[4px] bg-[#FEF2F2] text-[#D8210C]">Cancelling</span>
  );
  if (user.hasActiveSubscription) return (
    <span className="text-[11px] font-semibold px-[6px] py-[1px] rounded-[4px] bg-[#1D1D1E] text-white">{user.subscriptionTier}</span>
  );
  return (
    <span className="text-[11px] font-semibold px-[6px] py-[1px] rounded-[4px] bg-[#EFEFF1] text-[#818185]">Free</span>
  );
};

type ModalState =
  | { type: 'account-actions'; user: IAdminUser }
  | { type: 'delete'; user: IAdminUser }
  | { type: 'notes'; user: IAdminUser }
  | { type: 'login-history'; user: IAdminUser }
  | null;

export const SiteAdminPage = () => {
  const { t } = useTranslation();
  const { isAdmin } = useAuth();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const [search, setSearch] = React.useState('');
  const [modal, setModal] = React.useState<ModalState>(null);
  const [notesText, setNotesText] = React.useState('');
  const [sortKey, setSortKey] = React.useState<string>('createdAt');
  const [sortDir, setSortDir] = React.useState<'asc' | 'desc'>('desc');
  const [filter, setFilter] = React.useState<string>('all');
  const [page, setPage] = React.useState(1);
  const [emailOnDelete, setEmailOnDelete] = React.useState(true);
  const PAGE_SIZE = 25;

  const usersQuery = useQuery({
    queryKey: ['site-admin-users'],
    queryFn: () => adminService.getUsers(),
    enabled: isAdmin
  });

  const resendActivationMutation = useMutation({
    mutationFn: (userId: number) => adminService.resendActivation(userId),
    onSuccess: () => {
      toast.success(t('siteAdmin.activationSent'));
      setModal(null);
    },
    onError: () => toast.error(t('siteAdmin.activationSentFailed'))
  });

  const forceActivateMutation = useMutation({
    mutationFn: (userId: number) => adminService.forceActivate(userId),
    onSuccess: () => {
      toast.success(t('siteAdmin.accountActivated'));
      queryClient.invalidateQueries({ queryKey: ['site-admin-users'] });
      setModal(null);
    },
    onError: () => toast.error(t('siteAdmin.accountActivateFailed'))
  });

  const loginHistoryUserId = modal?.type === 'login-history' ? modal.user.id : null;
  const loginHistoryQuery = useQuery({
    queryKey: ['login-history', loginHistoryUserId],
    queryFn: () => adminService.getLoginHistory(loginHistoryUserId!),
    enabled: loginHistoryUserId !== null
  });

  const updateNotesMutation = useMutation({
    mutationFn: ({ userId, notes }: { userId: number; notes: string }) =>
      adminService.updateNotes(userId, notes),
    onSuccess: () => {
      toast.success(t('siteAdmin.noteSaved'));
      queryClient.invalidateQueries({ queryKey: ['site-admin-users'] });
      setModal(null);
    },
    onError: () => toast.error(t('siteAdmin.noteSaveFailed'))
  });

  const deleteUserMutation = useMutation({
    mutationFn: (userId: number) => adminService.deleteUser(userId, emailOnDelete),
    onSuccess: () => {
      toast.success(t('siteAdmin.accountDeleted'));
      queryClient.invalidateQueries({ queryKey: ['site-admin-users'] });
      setModal(null);
    },
    onError: () => toast.error(t('siteAdmin.accountDeleteFailed'))
  });

  const toggleProGiftedMutation = useMutation({
    mutationFn: (userId: number) => adminService.toggleProGifted(userId),
    onSuccess: () => {
      toast.success('Pro status updated');
      queryClient.invalidateQueries({ queryKey: ['site-admin-users'] });
    },
    onError: () => toast.error('Failed to update pro status')
  });

  const resetTrialMutation = useMutation({
    mutationFn: (userId: number) => adminService.resetFreeTrial(userId),
    onSuccess: () => {
      toast.success('Free trial reset');
      queryClient.invalidateQueries({ queryKey: ['site-admin-users'] });
    },
    onError: () => toast.error('Failed to reset trial')
  });

  const toggleEnterpriseMutation = useMutation({
    mutationFn: (userId: number) => adminService.toggleEnterprise(userId),
    onSuccess: () => {
      toast.success('Enterprise status updated');
      queryClient.invalidateQueries({ queryKey: ['site-admin-users'] });
    },
    onError: () => toast.error('Failed to update enterprise status')
  });

  const [inviteEmail, setInviteEmail] = React.useState('');
  const [confirmingInvite, setConfirmingInvite] = React.useState<string | null>(null);
  const sendInviteMutation = useMutation({
    mutationFn: (email: string) => adminService.sendProInvite(email),
    onSuccess: () => {
      toast.success(`Pro invite sent to ${confirmingInvite}`);
      setInviteEmail('');
      setConfirmingInvite(null);
    },
    onError: () => {
      toast.error('Failed to send invite');
      setConfirmingInvite(null);
    }
  });

  React.useEffect(() => {
    if (!isAdmin) navigate(ROUTER_LINKS.ROOT);
  }, [isAdmin, navigate]);

  React.useEffect(() => {
    if (usersQuery.error) toast.error(t('siteAdmin.loadUsersFailed'));
  }, [usersQuery.error]);

  if (!isAdmin) return null;

  const allUsers = usersQuery.data ?? [];
  const now = new Date();

  const filteredUsers = React.useMemo(() => {
    let users = allUsers;
    if (search.trim()) users = users.filter((u) => u.email.toLowerCase().includes(search.toLowerCase()));
    if (filter === 'unactivated') users = users.filter((u) => !u.isActivated);
    else if (filter === 'paid') users = users.filter((u) => u.subscriptionStatus === 'active');
    else if (filter === 'trial') users = users.filter((u) => u.subscriptionStatus === 'trialing');
    else if (filter === 'expired') users = users.filter((u) => u.subscriptionStatus === 'canceled' || u.subscriptionStatus === 'past_due');
    else if (filter === 'locked') users = users.filter((u) => u.isLocked);
    else if (filter === 'enterprise') users = users.filter((u) => u.subscriptionTier === 'enterprise');

    users = [...users].sort((a, b) => {
      let av: any, bv: any;
      if (sortKey === 'email') { av = a.email.toLowerCase(); bv = b.email.toLowerCase(); }
      else if (sortKey === 'createdAt') { av = a.createdAt; bv = b.createdAt; }
      else if (sortKey === 'status') { av = a.isActivated ? 1 : 0; bv = b.isActivated ? 1 : 0; }
      else if (sortKey === 'subscription') { av = a.subscriptionStatus ?? ''; bv = b.subscriptionStatus ?? ''; }
      else if (sortKey === 'expires') { av = a.subscriptionEndsAt ?? ''; bv = b.subscriptionEndsAt ?? ''; }
      else if (sortKey === 'lastLogin') { av = a.lastLoginAt ?? ''; bv = b.lastLoginAt ?? ''; }
      else { av = a.createdAt; bv = b.createdAt; }
      if (av < bv) return sortDir === 'asc' ? -1 : 1;
      if (av > bv) return sortDir === 'asc' ? 1 : -1;
      return 0;
    });
    return users;
  }, [allUsers, search, filter, sortKey, sortDir]);

  const totalPages = Math.max(1, Math.ceil(filteredUsers.length / PAGE_SIZE));
  const pagedUsers = filteredUsers.slice((page - 1) * PAGE_SIZE, page * PAGE_SIZE);

  React.useEffect(() => { setPage(1); }, [search, filter, sortKey, sortDir]);

  const handleSort = (key: string) => {
    if (sortKey === key) setSortDir((d) => (d === 'asc' ? 'desc' : 'asc'));
    else { setSortKey(key); setSortDir('desc'); }
  };

  const SortHeader = ({ label, sortId, className }: { label: string; sortId: string; className: string }) => (
    <th
      onClick={() => handleSort(sortId)}
      className={`${className} cursor-pointer select-none hover:text-[#111827] transition-colors`}
    >
      {label} {sortKey === sortId ? (sortDir === 'asc' ? '▲' : '▼') : ''}
    </th>
  );

  const isPending =
    resendActivationMutation.isPending ||
    forceActivateMutation.isPending ||
    deleteUserMutation.isPending ||
    updateNotesMutation.isPending ||
    toggleProGiftedMutation.isPending ||
    resetTrialMutation.isPending;

  const statCards = React.useMemo(() => [
    { label: t('siteAdmin.total'),       value: allUsers.length },
    { label: t('siteAdmin.verified'),    value: allUsers.filter((u) => u.isActivated).length },
    { label: t('siteAdmin.unactivated'), value: allUsers.filter((u) => !u.isActivated).length },
    { label: t('siteAdmin.paid'),        value: allUsers.filter((u) => u.subscriptionStatus === 'active').length },
    { label: t('siteAdmin.locked'),      value: allUsers.filter((u) => u.isLocked).length },
  ], [allUsers]);

  return (
    <div className="w-full max-w-[1400px] mx-auto py-[40px] px-[20px]">

      {/* Modal */}
      {modal && (
        <div
          className="fixed inset-0 z-50 flex items-center justify-center bg-black/30 backdrop-blur-[3px]"
          onClick={() => setModal(null)}
        >
          <div
            onClick={(e) => e.stopPropagation()}
            className={`bg-white rounded-[20px] shadow-xl px-[32px] py-[28px] w-full mx-[20px] ${
              modal.type === 'login-history' ? 'max-w-[540px]' : 'max-w-[420px]'
            }`}
          >
            {modal.type === 'account-actions' && (
              <>
                <h2 className="text-[18px] font-display font-bold text-[#111827] mb-[4px]">{t('siteAdmin.accountActions')}</h2>
                <p className="text-[14px] text-gray-700 mb-[20px]">
                  <span className="font-medium text-[#111827]">{modal.user.email}</span> (unactivated)
                </p>
                <div className="flex flex-col gap-[10px] mb-[20px]">
                  <button
                    onClick={() => resendActivationMutation.mutate(modal.user.id)}
                    disabled={isPending}
                    className="w-full flex items-center gap-[10px] px-[14px] py-[10px] rounded-[10px] border border-blue-200 bg-blue-50 hover:bg-blue-100 transition-colors text-left disabled:opacity-50"
                  >
                    <span className="text-[16px]">📧</span>
                    <div>
                      <p className="text-[13px] font-medium text-blue-800">{t('siteAdmin.resendActivation')}</p>
                      <p className="text-[11px] text-blue-600">{t('siteAdmin.resendActivationDesc')}</p>
                    </div>
                  </button>
                  <button
                    onClick={() => forceActivateMutation.mutate(modal.user.id)}
                    disabled={isPending}
                    className="w-full flex items-center gap-[10px] px-[14px] py-[10px] rounded-[10px] border border-green-200 bg-green-50 hover:bg-green-100 transition-colors text-left disabled:opacity-50"
                  >
                    <span className="text-[16px]">✅</span>
                    <div>
                      <p className="text-[13px] font-medium text-green-800">{t('siteAdmin.forceActivate')}</p>
                      <p className="text-[11px] text-green-600">{t('siteAdmin.forceActivateDesc')}</p>
                    </div>
                  </button>
                </div>
                <div className="flex justify-end">
                  <button
                    onClick={() => setModal(null)}
                    className="px-[18px] py-[8px] rounded-[10px] text-[14px] font-medium text-gray-700 border border-gray-200 hover:bg-gray-50 transition-colors"
                  >
                    {t('common.close')}
                  </button>
                </div>
              </>
            )}

            {modal.type === 'delete' && (
              <>
                <h2 className="text-[18px] font-display font-bold text-[#111827] mb-[8px]">{t('siteAdmin.deleteAccountTitle')}</h2>
                <p className="text-[14px] text-gray-700 mb-[6px]">
                  This will permanently delete{' '}
                  <span className="font-medium text-[#111827]">{modal.user.email}</span> and all associated data.
                </p>
                <p className="text-[13px] text-red-500 font-medium mb-[16px]">{t('siteAdmin.cannotBeUndone')}</p>
                <label className="flex items-center gap-[8px] mb-[24px] cursor-pointer">
                  <input
                    type="checkbox"
                    checked={emailOnDelete}
                    onChange={(e) => setEmailOnDelete(e.target.checked)}
                    className="w-[16px] h-[16px] rounded accent-red-500"
                  />
                  <span className="text-[13px] text-gray-700">{t('siteAdmin.emailOnDelete')}</span>
                </label>
                <div className="flex gap-[10px] justify-end">
                  <button
                    onClick={() => setModal(null)}
                    className="px-[18px] py-[8px] rounded-[10px] text-[14px] font-medium text-gray-700 border border-gray-200 hover:bg-gray-50 transition-colors"
                  >
                    {t('common.cancel')}
                  </button>
                  <button
                    onClick={() => deleteUserMutation.mutate(modal.user.id)}
                    disabled={isPending}
                    className="px-[18px] py-[8px] rounded-[10px] text-[14px] font-medium bg-red-500 text-white hover:bg-red-600 disabled:opacity-50 transition-colors"
                  >
                    {isPending ? t('settings.deleting') : t('siteAdmin.deleteAccountButton')}
                  </button>
                </div>
              </>
            )}

            {modal.type === 'login-history' && (
              <>
                <div className="flex items-start justify-between mb-[16px]">
                  <div>
                    <h2 className="text-[18px] font-display font-bold text-[#111827]">{t('siteAdmin.loginHistory')}</h2>
                    <p className="text-[13px] text-gray-700 mt-[2px] truncate max-w-[320px]">{modal.user.email}</p>
                  </div>
                  <button
                    onClick={() => setModal(null)}
                    className="text-gray-600 hover:text-gray-900 text-[22px] leading-none mt-[2px] font-medium"
                  >
                    ×
                  </button>
                </div>
                {loginHistoryQuery.isLoading ? (
                  <div className="flex justify-center py-[32px]">
                    <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-[#0ea5c9]" />
                  </div>
                ) : (loginHistoryQuery.data ?? []).length === 0 ? (
                  <p className="text-[14px] text-gray-700 text-center py-[24px]">{t('siteAdmin.noLoginEvents')}</p>
                ) : (
                  <div className="flex flex-col gap-[6px] max-h-[400px] overflow-y-auto pr-[4px]">
                    {(loginHistoryQuery.data as ILoginEvent[]).map((event) => (
                      <div
                        key={event.id}
                        className={`flex items-start gap-[10px] px-[12px] py-[10px] rounded-[10px] border ${
                          event.success ? 'bg-white border-[rgba(0,0,0,0.08)]' : 'bg-red-50 border-red-200'
                        }`}
                      >
                        <span
                          className={`mt-[3px] w-[7px] h-[7px] rounded-full shrink-0 ${
                            event.success ? 'bg-green-500' : 'bg-red-500'
                          }`}
                        />
                        <div className="flex-1 min-w-0">
                          <div className="flex items-center justify-between gap-[8px]">
                            <span className="font-mono text-[11px] text-gray-700">{event.ipAddress ?? '—'}</span>
                            <span className="text-[11px] text-gray-500 shrink-0">{formatDateTime(event.createdAt)}</span>
                          </div>
                          <div className="text-[11px] text-gray-500 mt-[2px]">{parseUserAgent(event.userAgent)}</div>
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </>
            )}

            {modal.type === 'notes' && (
              <>
                <h2 className="text-[18px] font-display font-bold text-[#111827] mb-[4px]">{t('siteAdmin.noteLabel')}</h2>
                <p className="text-[13px] text-gray-700 mb-[14px] truncate">{modal.user.email}</p>
                <textarea
                  autoFocus
                  value={notesText}
                  onChange={(e) => setNotesText(e.target.value)}
                  maxLength={1000}
                  rows={5}
                  className="w-full rounded-[10px] border border-[rgba(0,0,0,0.12)] px-[14px] py-[10px] text-[14px] text-[#111827] resize-none focus:outline-none focus:ring-2 focus:ring-[#0ea5c9]/30 mb-[16px]"
                  placeholder={t('siteAdmin.notePlaceholder')}
                />
                <div className="flex gap-[10px] justify-end">
                  <button
                    onClick={() => setModal(null)}
                    className="px-[18px] py-[8px] rounded-[10px] text-[14px] font-medium text-gray-700 border border-gray-200 hover:bg-gray-50 transition-colors"
                  >
                    {t('common.cancel')}
                  </button>
                  <button
                    onClick={() => updateNotesMutation.mutate({ userId: modal.user.id, notes: notesText })}
                    disabled={isPending}
                    className="px-[18px] py-[8px] rounded-[10px] text-[14px] font-medium bg-[#0ea5c9] text-white hover:bg-[#0882a0] disabled:opacity-50 transition-colors"
                  >
                    {isPending ? t('siteAdmin.saving') : t('common.save')}
                  </button>
                </div>
              </>
            )}
          </div>
        </div>
      )}

      {/* Header */}
      <div className="flex items-center justify-between mb-[24px] flex-wrap gap-[12px]">
        <h1 className="text-[28px] font-display font-bold text-[#111827] tracking-tight">{t('siteAdmin.title')}</h1>
        <button
          onClick={() => navigate(ROUTER_LINKS.ADMIN_CHANGELOG)}
          className="text-[13px] text-[#0ea5c9] hover:underline transition-colors"
        >
          📋 {t('siteAdmin.changelog')}
        </button>
      </div>

      {/* Summary stats */}
      {allUsers.length > 0 && (
        <div className="grid grid-cols-2 tablet:grid-cols-6 gap-[12px] mb-[20px]">
          {statCards.map((s) => (
            <div
              key={s.label}
              className="bg-white rounded-[12px] border border-[rgba(0,0,0,0.18)] px-[16px] py-[12px] shadow-sm"
            >
              <p className="text-[11px] font-semibold text-gray-600 uppercase tracking-wider">{s.label}</p>
              <p className="text-[22px] font-bold text-[#111827] mt-[2px]">{s.value}</p>
            </div>
          ))}
        </div>
      )}

      {/* Send Pro Invite */}
      <div className="bg-white rounded-[12px] border border-[rgba(0,0,0,0.18)] px-[16px] py-[14px] shadow-sm mb-[20px]">
        <p className="text-[11px] font-semibold text-gray-600 uppercase tracking-wider mb-[8px]">Send Pro Invite</p>
        {!confirmingInvite ? (
          <form
            onSubmit={(e) => {
              e.preventDefault();
              if (inviteEmail.trim()) setConfirmingInvite(inviteEmail.trim());
            }}
            className="flex items-center gap-[10px]"
          >
            <input
              type="email"
              placeholder="user@example.com"
              value={inviteEmail}
              onChange={(e) => setInviteEmail(e.target.value)}
              required
              className="h-[38px] flex-1 max-w-[320px] px-[14px] rounded-[10px] border border-[rgba(0,0,0,0.25)] text-[14px] text-[#111827] placeholder-gray-500 bg-white shadow-sm focus:outline-none focus:ring-2 focus:ring-[#0ea5c9]/30"
            />
            <button
              type="submit"
              disabled={!inviteEmail.trim()}
              className="h-[38px] px-[18px] rounded-[10px] text-[14px] font-medium bg-[#0ea5c9] text-white hover:bg-[#0882a0] disabled:opacity-50 transition-colors"
            >
              Send Pro Invite
            </button>
          </form>
        ) : (
          <div className="p-[14px] rounded-[12px] bg-[#F8FCFF] border border-[#BCEBFB]">
            <p className="font-body text-[14px] text-base-black mb-[2px]">
              Send Pro gifted invite to:
            </p>
            <p className="font-mono text-[14px] font-semibold text-[#0ea5c9] mb-[12px]">
              {confirmingInvite}
            </p>
            <div className="flex items-center gap-[8px]">
              <button
                type="button"
                onClick={() => sendInviteMutation.mutate(confirmingInvite)}
                disabled={sendInviteMutation.isPending}
                className={`h-[36px] px-[16px] rounded-[10px] text-[13px] font-medium text-white transition-colors ${
                  sendInviteMutation.isPending ? 'bg-gray-400 opacity-50' : 'bg-[#0ea5c9] hover:bg-[#0882a0]'
                }`}
              >
                {sendInviteMutation.isPending ? 'Sending...' : 'Confirm'}
              </button>
              <button
                type="button"
                onClick={() => setConfirmingInvite(null)}
                disabled={sendInviteMutation.isPending}
                className="h-[36px] px-[16px] rounded-[10px] text-[13px] font-medium border border-[rgba(0,0,0,0.15)] text-[#4A4A4C] hover:bg-[#F5F5F6] transition-colors"
              >
                Cancel
              </button>
            </div>
          </div>
        )}
      </div>

      {/* Create Company */}
      <CompanyManager />

      {/* Search + filters */}
      <div className="flex items-center justify-between mb-[12px] gap-[12px] flex-wrap">
        <div className="relative w-full tablet:w-[300px]">
          <span className="absolute left-[12px] top-1/2 -translate-y-1/2 text-[14px]">🔍</span>
          <input
            type="text"
            placeholder={t('siteAdmin.searchPlaceholder')}
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            className="h-[38px] w-full pl-[36px] pr-[14px] rounded-[10px] border border-[rgba(0,0,0,0.25)] text-[14px] text-[#111827] placeholder-gray-500 bg-white shadow-sm focus:outline-none focus:ring-2 focus:ring-[#0ea5c9]/30"
          />
        </div>
      </div>

      <div className="flex gap-[6px] mb-[16px] flex-wrap">
        {[
          { id: 'all',         label: t('siteAdmin.all') },
          { id: 'unactivated', label: t('siteAdmin.unactivated') },
          { id: 'paid',        label: t('siteAdmin.paid') },
          { id: 'expired',     label: t('siteAdmin.expired') },
          { id: 'locked',      label: t('siteAdmin.locked') },
          { id: 'enterprise',  label: 'Enterprise' },
        ].map((f) => (
          <button
            key={f.id}
            onClick={() => setFilter(f.id)}
            className={`px-[12px] py-[5px] rounded-[8px] text-[12px] font-medium border transition-colors ${
              filter === f.id
                ? 'bg-[#0ea5c9] text-white border-[#0ea5c9]'
                : 'bg-white text-gray-700 border-[rgba(0,0,0,0.25)] hover:bg-gray-100'
            }`}
          >
            {f.label}
          </button>
        ))}
      </div>

      {/* Table */}
      <div className="w-full rounded-[18px] border border-[rgba(0,0,0,0.11)] bg-white shadow-[0_4px_16px_rgba(0,0,0,0.08),0_1px_4px_rgba(0,0,0,0.04)] overflow-hidden relative">
        <div className="absolute top-0 left-0 right-0 h-[3px] bg-gradient-to-r from-[#0ea5c9] to-[#6d5fc0] rounded-t-[18px]" />

        {usersQuery.isLoading ? (
          <div className="flex justify-center items-center py-16">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-[#0ea5c9]" />
          </div>
        ) : (
          <>
            {/* Desktop table */}
            <div className="hidden tablet:block overflow-x-auto">
              <table className="w-full text-sm min-w-[900px]">
                <thead>
                  <tr className="border-b border-[rgba(0,0,0,0.07)] bg-[#f9fafb] dark:bg-[#1C1C1F]">
                    <SortHeader label={t('siteAdmin.email')}        sortId="email"        className="text-left pl-[16px] pr-[10px] py-[14px] text-[12px] font-semibold text-gray-700 uppercase tracking-wider" />
                    <SortHeader label={t('siteAdmin.joined')}       sortId="createdAt"    className="text-left px-[10px] py-[14px] text-[12px] font-semibold text-gray-700 uppercase tracking-wider" />
                    <SortHeader label={t('siteAdmin.status')}       sortId="status"       className="text-left px-[10px] py-[14px] text-[12px] font-semibold text-gray-700 uppercase tracking-wider" />
                    <SortHeader label={t('siteAdmin.subscription')} sortId="subscription" className="text-left px-[10px] py-[14px] text-[12px] font-semibold text-gray-700 uppercase tracking-wider" />
                    <SortHeader label={t('siteAdmin.expires')}      sortId="expires"      className="text-left px-[10px] py-[14px] text-[12px] font-semibold text-gray-700 uppercase tracking-wider" />
                    <SortHeader label={t('siteAdmin.lastLogin')}   sortId="lastLogin"    className="text-left px-[10px] py-[14px] text-[12px] font-semibold text-gray-700 uppercase tracking-wider" />
                    <th className="text-center px-[10px] py-[14px] text-[12px] font-semibold text-gray-700 uppercase tracking-wider">{t('siteAdmin.notes')}</th>
                    <th className="px-[10px] py-[14px]"></th>
                  </tr>
                </thead>
                <tbody>
                  {pagedUsers.map((user: IAdminUser, i: number) => (
                    <tr
                      key={user.id}
                      className={`border-b border-[rgba(0,0,0,0.05)] dark:border-[rgba(255,255,255,0.06)] ${i % 2 === 0 ? 'bg-white dark:bg-[#1C1C1F]' : 'bg-[#fafafa] dark:bg-[#242427]'}`}
                    >
                      {/* Email */}
                      <td className="pl-[16px] pr-[10px] py-[14px] font-medium text-[#111827] max-w-[260px]">
                        <span className="flex items-center gap-[6px] flex-wrap">
                          <span className="truncate" title={user.email}>{user.email}</span>
                          {user.role === 'admin' && (
                            <span className="inline-flex items-center px-[6px] py-[1px] rounded-[4px] bg-purple-100 text-purple-700 text-[10px] font-semibold">
                              admin
                            </span>
                          )}
                          {user.isLocked && (
                            <span
                              title="Account is currently locked: 10+ failed attempts in the last 15 minutes"
                              className="inline-flex items-center gap-[4px] px-[6px] py-[1px] rounded-[4px] bg-red-600 text-white text-[10px] font-semibold cursor-default"
                            >
                              🔒 Locked
                            </span>
                          )}
                          {!user.isLocked && user.failedAttempts > 0 && (
                            <span
                              title={`${user.failedAttempts} failed login attempt${user.failedAttempts > 1 ? 's' : ''} in last 7 days`}
                              className="inline-flex items-center gap-[4px] px-[6px] py-[1px] rounded-[4px] bg-amber-100 text-amber-700 text-[10px] font-semibold cursor-default"
                            >
                              ⚠ {user.failedAttempts}
                            </span>
                          )}
                        </span>
                      </td>

                      {/* Joined */}
                      <td className="px-[10px] py-[14px] text-gray-700 whitespace-nowrap">{formatDate(user.createdAt)}</td>

                      {/* Activation status */}
                      <td className="px-[10px] py-[14px]">
                        {user.isActivated ? (
                          <span className="inline-flex items-center gap-[5px] text-green-700 font-medium">
                            <span className="w-[6px] h-[6px] rounded-full bg-green-500" />
                            {t('siteAdmin.verified')}
                          </span>
                        ) : (
                          <span className="inline-flex items-center gap-[6px]">
                            <span className="w-[6px] h-[6px] rounded-full bg-red-500 shrink-0" />
                            <button
                              onClick={() => setModal({ type: 'account-actions', user })}
                              title="Account actions"
                              className="text-[16px] leading-none hover:opacity-70 transition-opacity"
                            >
                              ⚙️
                            </button>
                          </span>
                        )}
                      </td>

                      {/* Subscription badge */}
                      <td className="px-[10px] py-[14px]">
                        <SubscriptionBadge user={user} />
                      </td>

                      {/* Expiry */}
                      <td className="px-[10px] py-[14px] text-gray-700 whitespace-nowrap">
                        {user.subscriptionEndsAt ? (
                          <span className={`text-[13px] inline-flex items-center gap-[4px] ${new Date(user.subscriptionEndsAt) >= now ? '' : 'text-red-500'}`}>
                            <span className={`w-[5px] h-[5px] rounded-full ${new Date(user.subscriptionEndsAt) >= now ? 'bg-green-500' : 'bg-red-400'}`} />
                            {formatDate(user.subscriptionEndsAt)}
                          </span>
                        ) : (
                          <span className="text-gray-400">—</span>
                        )}
                      </td>

                      {/* Last login */}
                      <td className="px-[10px] py-[14px] whitespace-nowrap">
                        <button
                          onClick={() => setModal({ type: 'login-history', user })}
                          className="text-[13px] text-gray-700 hover:text-[#0ea5c9] hover:underline transition-colors text-left"
                        >
                          {user.lastLoginAt ? formatDate(user.lastLoginAt) : <span className="text-gray-400">—</span>}
                        </button>
                      </td>

                      {/* Notes */}
                      <td className="px-[10px] py-[14px] text-center">
                        <button
                          onClick={() => { setNotesText(user.adminNotes ?? ''); setModal({ type: 'notes', user }); }}
                          title={user.adminNotes ?? 'Add note'}
                          className={`text-[18px] leading-none transition-opacity hover:opacity-70 ${user.adminNotes ? 'opacity-100' : 'opacity-30'}`}
                        >
                          📝
                        </button>
                      </td>

                      {/* Actions */}
                      <td className="px-[10px] py-[14px] text-right">
                        <div className="flex items-center justify-end gap-[6px]">
                          <button
                            onClick={() => toggleProGiftedMutation.mutate(user.id)}
                            disabled={toggleProGiftedMutation.isPending}
                            className={`inline-flex items-center justify-center px-[8px] py-[3px] rounded-[6px] text-[11px] font-medium border transition-colors disabled:opacity-50 ${
                              user.isProGifted
                                ? 'bg-yellow-50 border-yellow-300 text-yellow-700 hover:bg-yellow-100'
                                : 'bg-gray-50 border-gray-300 text-gray-600 hover:bg-gray-100'
                            }`}
                            title={user.isProGifted ? 'Remove Pro access' : 'Grant Pro access'}
                          >
                            {user.isProGifted ? 'Remove Pro' : 'Grant Pro'}
                          </button>
                          <button
                            onClick={() => toggleEnterpriseMutation.mutate(user.id)}
                            disabled={toggleEnterpriseMutation.isPending}
                            className={`inline-flex items-center justify-center px-[8px] py-[3px] rounded-[6px] text-[11px] font-medium border transition-colors disabled:opacity-50 ${
                              user.subscriptionTier === 'enterprise'
                                ? 'bg-purple-50 border-purple-300 text-purple-700 hover:bg-purple-100'
                                : 'bg-gray-50 border-gray-300 text-gray-600 hover:bg-gray-100'
                            }`}
                            title={user.subscriptionTier === 'enterprise' ? 'Remove Enterprise' : 'Grant Enterprise'}
                          >
                            {user.subscriptionTier === 'enterprise' ? 'Remove Ent.' : 'Grant Ent.'}
                          </button>
                          {!user.hasFreeTrialAbility && (
                            <button
                              onClick={() => resetTrialMutation.mutate(user.id)}
                              disabled={resetTrialMutation.isPending}
                              className="inline-flex items-center justify-center px-[8px] py-[3px] rounded-[6px] text-[11px] font-medium bg-cyan-50 border border-cyan-300 text-cyan-700 hover:bg-cyan-100 transition-colors disabled:opacity-50"
                              title="Reset free trial availability"
                            >
                              Reset Trial
                            </button>
                          )}
                          {user.role !== 'admin' ? (
                            <button
                              onClick={() => setModal({ type: 'delete', user })}
                              className="inline-flex items-center justify-center px-[8px] py-[3px] rounded-[6px] text-[11px] font-medium bg-red-50 border border-red-300 text-red-600 hover:bg-red-100 transition-colors"
                            >
                              Delete
                            </button>
                          ) : (
                            <span title="Site admin" className="text-[18px]">⚡</span>
                          )}
                        </div>
                      </td>
                    </tr>
                  ))}
                  {filteredUsers.length === 0 && (
                    <tr>
                      <td colSpan={8} className="px-[24px] py-[32px] text-center text-gray-700 text-[14px]">
                        {search ? `No accounts match "${search}"` : t('siteAdmin.noResults')}
                      </td>
                    </tr>
                  )}
                </tbody>
              </table>
            </div>

            {/* Mobile card layout */}
            <div className="tablet:hidden flex flex-col">
              {pagedUsers.map((user: IAdminUser, i: number) => (
                <div
                  key={user.id}
                  className={`px-[16px] py-[14px] border-b border-[rgba(0,0,0,0.05)] dark:border-[rgba(255,255,255,0.06)] ${i % 2 === 0 ? 'bg-white dark:bg-[#1C1C1F]' : 'bg-[#fafafa] dark:bg-[#242427]'}`}
                >
                  <div className="flex items-start justify-between gap-[8px] mb-[10px]">
                    <div className="flex items-center gap-[6px] flex-wrap min-w-0">
                      <span className="font-medium text-[14px] text-[#111827] break-all">{user.email}</span>
                      {user.role === 'admin' && (
                        <span className="inline-flex items-center px-[6px] py-[1px] rounded-[4px] bg-purple-100 text-purple-700 text-[10px] font-semibold shrink-0">
                          admin
                        </span>
                      )}
                      {user.isLocked && (
                        <span className="inline-flex items-center gap-[4px] px-[6px] py-[1px] rounded-[4px] bg-red-600 text-white text-[10px] font-semibold shrink-0">
                          🔒 Locked
                        </span>
                      )}
                      {!user.isLocked && user.failedAttempts > 0 && (
                        <span className="inline-flex items-center gap-[4px] px-[6px] py-[1px] rounded-[4px] bg-amber-100 text-amber-700 text-[10px] font-semibold shrink-0">
                          ⚠ {user.failedAttempts}
                        </span>
                      )}
                    </div>
                    <div className="flex items-center gap-[6px] shrink-0">
                      <button
                        onClick={() => toggleProGiftedMutation.mutate(user.id)}
                        disabled={toggleProGiftedMutation.isPending}
                        className={`inline-flex items-center justify-center px-[8px] py-[3px] rounded-[6px] text-[11px] font-medium border transition-colors disabled:opacity-50 ${
                          user.isProGifted
                            ? 'bg-yellow-50 border-yellow-300 text-yellow-700 hover:bg-yellow-100'
                            : 'bg-gray-50 border-gray-300 text-gray-600 hover:bg-gray-100'
                        }`}
                        title={user.isProGifted ? 'Remove Pro access' : 'Grant Pro access'}
                      >
                        {user.isProGifted ? 'Remove Pro' : 'Grant Pro'}
                      </button>
                      <button
                        onClick={() => toggleEnterpriseMutation.mutate(user.id)}
                        disabled={toggleEnterpriseMutation.isPending}
                        className={`inline-flex items-center justify-center px-[8px] py-[3px] rounded-[6px] text-[11px] font-medium border transition-colors disabled:opacity-50 ${
                          user.subscriptionTier === 'enterprise'
                            ? 'bg-purple-50 border-purple-300 text-purple-700 hover:bg-purple-100'
                            : 'bg-gray-50 border-gray-300 text-gray-600 hover:bg-gray-100'
                        }`}
                        title={user.subscriptionTier === 'enterprise' ? 'Remove Enterprise' : 'Grant Enterprise'}
                      >
                        {user.subscriptionTier === 'enterprise' ? 'Remove Ent.' : 'Grant Ent.'}
                      </button>
                      {!user.hasFreeTrialAbility && (
                        <button
                          onClick={() => resetTrialMutation.mutate(user.id)}
                          disabled={resetTrialMutation.isPending}
                          className="inline-flex items-center justify-center px-[8px] py-[3px] rounded-[6px] text-[11px] font-medium bg-cyan-50 border border-cyan-300 text-cyan-700 hover:bg-cyan-100 transition-colors disabled:opacity-50"
                          title="Reset free trial availability"
                        >
                          Reset Trial
                        </button>
                      )}
                      <button
                        onClick={() => { setNotesText(user.adminNotes ?? ''); setModal({ type: 'notes', user }); }}
                        title={user.adminNotes ?? 'Add note'}
                        className={`text-[16px] leading-none transition-opacity hover:opacity-70 ${user.adminNotes ? 'opacity-100' : 'opacity-30'}`}
                      >
                        📝
                      </button>
                      {user.role !== 'admin' ? (
                        <button
                          onClick={() => setModal({ type: 'delete', user })}
                          className="inline-flex items-center justify-center px-[8px] py-[3px] rounded-[6px] text-[11px] font-medium bg-red-50 border border-red-300 text-red-600 hover:bg-red-100 transition-colors"
                        >
                          {t('common.delete')}
                        </button>
                      ) : (
                        <span title="Site admin" className="text-[16px]">⚡</span>
                      )}
                    </div>
                  </div>

                  <div className="grid grid-cols-2 gap-x-[12px] gap-y-[8px] text-[13px]">
                    <div>
                      <span className="text-[11px] text-gray-500 uppercase tracking-wider font-semibold">{t('siteAdmin.joined')}</span>
                      <div className="text-gray-700">{formatDate(user.createdAt)}</div>
                    </div>
                    <div>
                      <span className="text-[11px] text-gray-500 uppercase tracking-wider font-semibold">{t('siteAdmin.status')}</span>
                      <div>
                        {user.isActivated ? (
                          <span className="inline-flex items-center gap-[5px] text-green-700 font-medium">
                            <span className="w-[6px] h-[6px] rounded-full bg-green-500" />
                            {t('siteAdmin.verified')}
                          </span>
                        ) : (
                          <span className="inline-flex items-center gap-[6px]">
                            <span className="w-[6px] h-[6px] rounded-full bg-red-500 shrink-0" />
                            <button
                              onClick={() => setModal({ type: 'account-actions', user })}
                              title="Account actions"
                              className="text-[14px] leading-none hover:opacity-70 transition-opacity"
                            >
                              ⚙️
                            </button>
                          </span>
                        )}
                      </div>
                    </div>
                    <div>
                      <span className="text-[11px] text-gray-500 uppercase tracking-wider font-semibold">{t('siteAdmin.subscription')}</span>
                      <div className="mt-[2px]">
                        <SubscriptionBadge user={user} />
                      </div>
                    </div>
                    <div>
                      <span className="text-[11px] text-gray-500 uppercase tracking-wider font-semibold">{t('siteAdmin.expires')}</span>
                      <div className="text-gray-700">
                        {user.subscriptionEndsAt ? formatDate(user.subscriptionEndsAt) : '—'}
                      </div>
                    </div>
                    <div>
                      <span className="text-[11px] text-gray-500 uppercase tracking-wider font-semibold">{t('siteAdmin.lastLogin')}</span>
                      <div>
                        <button
                          onClick={() => setModal({ type: 'login-history', user })}
                          className="text-gray-700 hover:text-[#0ea5c9] hover:underline transition-colors"
                        >
                          {user.lastLoginAt ? formatDate(user.lastLoginAt) : <span className="text-gray-400">—</span>}
                        </button>
                      </div>
                    </div>
                  </div>
                </div>
              ))}
              {filteredUsers.length === 0 && (
                <div className="px-[24px] py-[32px] text-center text-gray-700 text-[14px]">
                  {search ? `No accounts match "${search}"` : t('siteAdmin.noResults')}
                </div>
              )}
            </div>

            {/* Pagination */}
            {totalPages > 1 && (
              <div className="flex items-center justify-between px-[16px] py-[12px] border-t border-[rgba(0,0,0,0.07)] bg-[#f9fafb]">
                <span className="text-[13px] text-gray-600">
                  {(page - 1) * PAGE_SIZE + 1}–{Math.min(page * PAGE_SIZE, filteredUsers.length)} of {filteredUsers.length}
                </span>
                <div className="flex gap-[6px]">
                  <button
                    onClick={() => setPage((p) => Math.max(1, p - 1))}
                    disabled={page === 1}
                    className="px-[12px] py-[5px] rounded-[8px] text-[12px] font-medium border border-[rgba(0,0,0,0.25)] bg-white text-gray-700 hover:bg-gray-100 disabled:opacity-40 disabled:cursor-not-allowed transition-colors"
                  >
                    ← {t('siteAdmin.prev')}
                  </button>
                  <button
                    onClick={() => setPage((p) => Math.min(totalPages, p + 1))}
                    disabled={page === totalPages}
                    className="px-[12px] py-[5px] rounded-[8px] text-[12px] font-medium border border-[rgba(0,0,0,0.25)] bg-white text-gray-700 hover:bg-gray-100 disabled:opacity-40 disabled:cursor-not-allowed transition-colors"
                  >
                    {t('siteAdmin.next')} →
                  </button>
                </div>
              </div>
            )}
          </>
        )}
      </div>
    </div>
  );
};

/* ─── Company Manager (site admin) ─── */
const CompanyManager = () => {
  const queryClient = useQueryClient();
  const [expanded, setExpanded] = React.useState(false);
  const [name, setName] = React.useState('');
  const [slug, setSlug] = React.useState('');
  const [ownerEmail, setOwnerEmail] = React.useState('');

  const companiesQuery = useQuery({
    queryKey: ['admin-companies'],
    queryFn: () => adminService.getAllCompanies(),
    enabled: expanded,
  });

  const usersQuery = useQuery({ queryKey: ['site-admin-users'], queryFn: () => adminService.getUsers() });
  const allUsers = usersQuery.data ?? [];

  const createMutation = useMutation({
    mutationFn: () => {
      const owner = allUsers.find((u) => u.email === ownerEmail.trim());
      if (!owner) throw new Error('Owner not found');
      return adminService.createCompany(name.trim(), slug.trim(), owner.id);
    },
    onSuccess: () => {
      toast.success('Company created');
      setName('');
      setSlug('');
      setOwnerEmail('');
      queryClient.invalidateQueries({ queryKey: ['admin-companies'] });
    },
    onError: (e: any) => toast.error(e?.response?.data?.message ?? e?.message ?? 'Failed to create company'),
  });

  const companies = (companiesQuery.data ?? []) as any[];

  return (
    <div className="bg-white rounded-[12px] border border-[rgba(0,0,0,0.18)] px-[16px] py-[14px] shadow-sm mb-[20px]">
      <button onClick={() => setExpanded(!expanded)} className="flex items-center gap-[6px] w-full text-left">
        <p className="text-[11px] font-semibold text-gray-600 uppercase tracking-wider">Companies</p>
        <span className="text-[11px] text-gray-400">{expanded ? '▼' : '▶'}</span>
      </button>

      {expanded && (
        <div className="mt-[12px]">
          {/* Create form */}
          <form onSubmit={(e) => { e.preventDefault(); createMutation.mutate(); }} className="flex flex-wrap items-center gap-[8px] mb-[16px]">
            <input type="text" placeholder="Company name" value={name} onChange={(e) => { setName(e.target.value); if (!slug || slug === toSlug(name)) setSlug(toSlug(e.target.value)); }}
              className="h-[38px] w-[180px] px-[12px] rounded-[10px] border border-[rgba(0,0,0,0.25)] text-[13px] focus:outline-none focus:ring-2 focus:ring-[#0ea5c9]/30" />
            <input type="text" placeholder="slug" value={slug} onChange={(e) => setSlug(e.target.value)}
              className="h-[38px] w-[140px] px-[12px] rounded-[10px] border border-[rgba(0,0,0,0.25)] text-[13px] font-mono focus:outline-none focus:ring-2 focus:ring-[#0ea5c9]/30" />
            <input type="email" placeholder="Owner email" value={ownerEmail} onChange={(e) => setOwnerEmail(e.target.value)}
              className="h-[38px] w-[220px] px-[12px] rounded-[10px] border border-[rgba(0,0,0,0.25)] text-[13px] focus:outline-none focus:ring-2 focus:ring-[#0ea5c9]/30" />
            <button type="submit" disabled={!name.trim() || !slug.trim() || !ownerEmail.trim() || createMutation.isPending}
              className="h-[38px] px-[16px] rounded-[10px] text-[13px] font-medium bg-[#7C3AED] text-white hover:bg-[#6D28D9] disabled:opacity-50 transition-colors">
              Create Company
            </button>
          </form>

          {/* Companies list */}
          {companies.length > 0 && (
            <div className="space-y-[6px]">
              {companies.map((c: any) => (
                <div key={c.id} className="flex items-center gap-[10px] p-[10px] rounded-[10px] border border-[rgba(0,0,0,0.06)] text-[13px]">
                  <span className="font-semibold text-[#111827]">{c.name}</span>
                  <span className="font-mono text-[11px] text-[#818185]">/{c.slug}</span>
                  <span className="text-[11px] text-[#818185]">{c._count?.members ?? 0} members</span>
                  <span className="text-[11px] text-[#818185]">{c._count?.reviews ?? 0} queues</span>
                  <span className="text-[11px] text-[#818185] ml-auto">Owner: {c.owner?.email ?? c.ownerId}</span>
                </div>
              ))}
            </div>
          )}
          {companies.length === 0 && !companiesQuery.isLoading && (
            <p className="text-[13px] text-[#818185]">No companies yet</p>
          )}
        </div>
      )}
    </div>
  );
};

const toSlug = (s: string) => s.toLowerCase().replace(/[^a-z0-9]+/g, '-').replace(/^-|-$/g, '');

export default SiteAdminPage;
